import { StyleSheet, View } from 'react-native';
import { AlertCircle, RotateCcw } from 'lucide-react-native';
import { tokens } from '../../theme/tokens';
import { AppButton } from '../ui/AppButton';
import { AppCard } from '../ui/AppCard';
import { AppText } from '../ui/AppText';

type WrongQuestionCardProps = {
  title: string;
  subject: string;
  knowledgePoint: string;
  retryCount: number;
  lastWrongAt: string;
  sourceLabel: string;
  masteryStatus: string;
  errorType: string;
  onPractice?: () => void;
};

export function WrongQuestionCard({
  title,
  subject,
  knowledgePoint,
  retryCount,
  lastWrongAt,
  sourceLabel,
  masteryStatus,
  errorType,
  onPractice,
}: WrongQuestionCardProps) {
  return (
    <AppCard style={styles.card}>
      <View style={styles.header}>
        <View style={styles.iconWrap}>
          <AlertCircle size={18} color={tokens.colors.danger} />
        </View>
        <View style={styles.copy}>
          <AppText variant="subtitle" weight="bold">
            {title}
          </AppText>
          <AppText variant="caption" tone="muted">
            {subject} · {knowledgePoint}
          </AppText>
        </View>
        <View style={styles.retryPill}>
          <AppText variant="caption" tone="danger" weight="bold">
            {retryCount} 次
          </AppText>
        </View>
      </View>

      <View style={styles.metaPillRow}>
        <View style={styles.statusPill}>
          <AppText variant="caption" tone="primary" weight="bold">
            {masteryStatus}
          </AppText>
        </View>
        <View style={styles.errorPill}>
          <AppText variant="caption" tone="danger" weight="bold">
            {errorType}
          </AppText>
        </View>
        <AppText variant="caption" tone="muted" style={styles.sourceLabel}>
          {sourceLabel}
        </AppText>
      </View>

      <View style={styles.footer}>
        <AppText variant="caption" tone="muted">
          最近错题：{lastWrongAt}
        </AppText>
        {onPractice ? (
          <AppButton
            label="再练一次"
            variant="secondary"
            onPress={onPractice}
            leftSlot={<RotateCcw size={16} color={tokens.colors.primary} />}
            style={styles.practiceButton}
          />
        ) : null}
      </View>
    </AppCard>
  );
}

const styles = StyleSheet.create({
  card: {
    gap: tokens.spacing.md,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: tokens.spacing.md,
  },
  iconWrap: {
    width: 36,
    height: 36,
    borderRadius: tokens.radius.full,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colors.dangerSoft,
  },
  copy: {
    flex: 1,
    gap: tokens.spacing.xs,
  },
  retryPill: {
    borderRadius: tokens.radius.full,
    paddingHorizontal: tokens.spacing.sm,
    paddingVertical: tokens.spacing.xs,
    backgroundColor: tokens.colors.dangerSoft,
  },
  metaPillRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: tokens.spacing.sm,
  },
  statusPill: {
    borderRadius: tokens.radius.full,
    paddingHorizontal: tokens.spacing.sm,
    paddingVertical: tokens.spacing.xs,
    backgroundColor: tokens.colors.surfaceMuted,
  },
  errorPill: {
    borderRadius: tokens.radius.full,
    paddingHorizontal: tokens.spacing.sm,
    paddingVertical: tokens.spacing.xs,
    backgroundColor: tokens.colors.dangerSoft,
  },
  sourceLabel: {
    flexShrink: 1,
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacing.md,
  },
  practiceButton: {
    minHeight: 36,
    paddingHorizontal: tokens.spacing.md,
  },
});
